import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import jwt_decode from 'jwt-decode';
import { AuthService } from './auth.service';
import { DataTimeService } from '../layout/shared/service/data-time.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ExtendTimeModalComponent } from '../layout/shared/component/extend-time-modal/extend-time-modal.component';

/**
 * Watches the fixed session ceiling and warns once before it is reached.
 *
 * The realm has SSO Session Idle = SSO Session Max, so a token refresh can never move the
 * expiry — there is nothing to extend, only something to warn about. Access-token renewal is
 * handled silently by AuthService and AuthInterceptor; this timer never refreshes.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthTimerService {

  private subscription: Subscription;
  private warningShown = false;
  private warningModal: NgbModalRef = null;
  /**
   * Emits true once the ceiling warning is up, false again when the session ends. The task-detail,
   * task-form and create-promote forms start an auto-save-draft countdown on true and cancel it
   * on false.
   */
  public modalShown$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private dataTimeService: DataTimeService,
    private modalService: NgbModal,
    private router: Router,
  ) { }

  startTimer() {
    this.subscription = timer(0, 1000).subscribe(() => {
      // In RxJS 6 a throw inside a next handler unsubscribes the source: one bad tick (e.g. the
      // modal container being torn down mid-navigation) would silently kill the watchdog — and
      // with it the ceiling warning, the forced logout and the forms' auto-save trigger.
      try {
        this.tick();
      } catch (e) {
        console.error('auth-timer tick failed', e);
      }
    });
  }

  private tick(): void {
    const expiresAt = this.sessionExpiresAt();

    // The timer starts at the app root, before login and after logout. "No session" is not an
    // error — it just means there is nothing to warn about, so keep ticking. The warning re-arms
    // here, and modalShown$ goes back to false so open forms cancel their auto-save countdown.
    if (expiresAt === null) {
      if (this.warningShown) {
        this.warningShown = false;
        this.modalShown$.next(false);
      }
      return;
    }

    const now = Date.now();
    if (now >= expiresAt) {
      this.stopTimer();
      this.closeWarning();
      this.authService.clearSession();
      this.router.navigateByUrl('/login');
      return;
    }

    this.dataTimeService.remainTime$.next(
      this.dataTimeService.getRemainingTime(new Date(expiresAt), new Date(now))
    );

    if (!this.warningShown && now + DataTimeService.nearlyTimer >= expiresAt) {
      this.showCeilingWarning();
    }
  }

  /** Epoch ms of the session ceiling, or null when no session is stored or the token is unreadable. */
  private sessionExpiresAt(): number | null {
    const refreshToken = this.authService.refreshToken;
    if (!refreshToken) {
      return null;
    }
    try {
      return (jwt_decode(refreshToken) as any).exp * 1000;
    } catch (e) {
      return null;
    }
  }

  /** Shown at most once per session: the ceiling cannot be pushed back, so re-prompting is noise. */
  private showCeilingWarning(): void {
    this.warningShown = true;
    this.modalShown$.next(true);
    this.warningModal = this.modalService.open(ExtendTimeModalComponent, {
      backdrop: 'static',
      keyboard: false
    });
  }

  private closeWarning(): void {
    if (this.warningModal) {
      this.warningModal.close();
      this.warningModal = null;
    }
  }

  stopTimer() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
